/**
 * 67.21 — left- and right-handed people (the owner, Oct 10: "currently everyone is left handed, would be good to have
 * right handed ability / models"). Presentation only: the sim fires from the body's centreline and nothing offsets the
 * muzzle sideways, so a person's hand changes what is drawn and where the camera rides, never an outcome.
 *
 * The person model is built left-handed (its gun arm on the figure's left; rendering/personModel.ts) and a right-handed
 * person is the same model mirrored — the grip, the support hand, the holster and the sling, the bandage, the drone's
 * tablet and the throw all swap sides together, and every held item works in both hands by construction.
 *
 * Who is which: the player's hand is a setting (default right; `orbituary-hand`); every other unit's comes from the
 * match seed and its id — about nine in ten right-handed — so a replay or a watch of the same match draws the same
 * hands. A possessed unit takes the player's hand.
 */
export type Hand = 'right' | 'left';

/** About one person in ten is left-handed. */
export const LEFT_HANDED_SHARE = 0.1;

const HAND_KEY = 'orbituary-hand';

export function loadPlayerHand(): Hand {
  try { return localStorage.getItem(HAND_KEY) === 'left' ? 'left' : 'right'; } catch { return 'right'; }
}
export function savePlayerHand(h: Hand): void {
  try { localStorage.setItem(HAND_KEY, h); } catch { /* storage unavailable */ }
}

/** A unit's hand from the match seed and its id (FNV-1a over both): the same in every replay of the match. */
export function handOf(seed: string, unitId: number): Hand {
  let h = 0x811c9dc5;
  const s = `${seed}#hand#${unitId}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return (h % 1000) / 1000 < LEFT_HANDED_SHARE ? 'left' : 'right';
}

/** Which side of the body the strong hand is on, as a sign along the body's right: +1 right, −1 left. */
export function handSide(h: Hand): 1 | -1 {
  return h === 'right' ? 1 : -1;
}
